import type { SetStateAction } from "react";
import { useCallback, useId, useMemo, useState } from "react";
import { useDateLocale } from "../../hooks/useDateLocale";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import type { IDateOrder, IsoDate } from "../../utils/date";
import { readInputDate, writeInputDate } from "../../utils/date";
import { Dropdown } from "../Dropdown";
import { DatePickerProvider } from "./context";
import { DatePickerContent } from "./DatePickerContent";
import { DatePickerTrigger } from "./DatePickerTrigger";
import type {
  IDatePickerContext as IDatePickerContextType,
  IDatePickerProps,
} from "./types";

/**
 * The text a new value or a new field order leaves in the field. A null value
 * keeps a partial text: typing reports null until the date is complete.
 */
const nextText = (
  text: string,
  value: IsoDate | null,
  order: IDateOrder,
  previousOrder: IDateOrder,
): string => {
  const shown = readInputDate(text, previousOrder);
  if (value === null) return shown === null ? text : "";
  return shown === value && order === previousOrder
    ? text
    : writeInputDate(value, order);
};

const DatePicker = (props: IDatePickerProps) => {
  const locale = useDateLocale();
  // On touch a tap on the text raises the keyboard; opening the calendar too
  // would squeeze it above the keyboard. The calendar button opens it instead.
  const isTouch = useMediaQuery("(pointer: coarse)");
  const value = props.value ?? null;
  const [open, setOpen] = useState(false);
  const [focusCalendar, setFocusCalendar] = useState(false);
  const [displayValue, setDisplayValue] = useState(() =>
    value === null ? "" : writeInputDate(value, locale.order),
  );

  // Owner writes and locale changes reach the text during render, not an effect pass.
  const [synced, setSynced] = useState({ value, order: locale.order });
  if (synced.value !== value || synced.order !== locale.order) {
    setSynced({ value, order: locale.order });
    setDisplayValue(nextText(displayValue, value, locale.order, synced.order));
  }

  const generatedId = useId();
  const baseId = props.id || generatedId;
  const triggerId = props.inputId || `${baseId}-input`;
  const contentId = `${baseId}-content`;

  const handleOpenChange = useCallback((next: SetStateAction<boolean>) => {
    setFocusCalendar(false);
    setOpen(next);
  }, []);

  const openCalendar = useCallback(() => {
    setFocusCalendar(true);
    setOpen(true);
  }, []);

  const context = useMemo((): IDatePickerContextType => {
    return {
      ...props,
      open,
      setOpen,
      displayValue,
      setDisplayValue,
      focusCalendar,
      openCalendar,
      triggerId,
      contentId,
      locale,
    };
  }, [
    open,
    displayValue,
    focusCalendar,
    openCalendar,
    triggerId,
    contentId,
    locale,
    props,
  ]);

  return (
    <DatePickerProvider value={context}>
      <Dropdown
        trigger={<DatePickerTrigger />}
        content={<DatePickerContent />}
        triggerId={triggerId}
        contentId={contentId}
        open={open}
        onOpenChange={handleOpenChange}
        disabled={props.disabled}
        interactions={isTouch ? ["dismiss"] : ["click", "dismiss"]}
        role="dialog"
        innerControl
        ariaLabel={props.ariaLabel}
        ariaLabelledby={props.ariaLabelledby}
        ariaDescribedby={props.ariaDescribedby}
        floatingFocusManager={{
          initialFocus: -1,
          restoreFocus: false,
        }}
        placement="bottom-start"
        offset={8}
      />
    </DatePickerProvider>
  );
};

export { DatePicker };
